// The amount screen drives its value from an in-app numpad rather than a text
// input, so every edit is one keypress and the whole grammar lives here.

export type NumpadDigit =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'

/** `clear` is a held delete key: the whole value goes in one stroke. */
export type NumpadKey = NumpadDigit | '.' | 'backspace' | 'clear'

const DECIMAL_POINT = '.'

/**
 * The typed amount after one keypress, as a decimal string.
 *
 * - A lone `.` is not a number any parser accepts, so the leading zero is
 *   written for the user; a second point, and any point at all on a 0-decimal
 *   token, is refused rather than swallowed later.
 * - `0` is a placeholder rather than a leading digit, so typing `5` gives `5`.
 * - Fraction digits stop at the token's `decimals`: past that the input is dust
 *   the transfer can't carry, and it would silently truncate at signing time.
 */
export function applyNumpadKey(
  value: string,
  key: NumpadKey,
  maxDecimals?: number,
): string {
  if (key === 'backspace') {
    return value.slice(0, -1)
  }

  if (key === 'clear') {
    return ''
  }

  if (key === DECIMAL_POINT) {
    if (value.includes(DECIMAL_POINT) || maxDecimals === 0) {
      return value
    }
    return value === '' ? `0${DECIMAL_POINT}` : `${value}${DECIMAL_POINT}`
  }

  const fraction = value.split(DECIMAL_POINT)[1]
  if (
    fraction !== undefined &&
    maxDecimals !== undefined &&
    fraction.length >= maxDecimals
  ) {
    return value
  }

  if (value === '0') {
    return key === '0' ? value : key
  }
  return `${value}${key}`
}
